import React, { useState, useEffect } from 'react';
import {
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  Tooltip,
} from '@mui/material';
import { DateRange as DateRangeIcon, Warning as WarningIcon } from '@mui/icons-material';
import { api } from '../services/api';
import { formatDateMedium } from '../utils/date';

interface DateRangeSelectorProps {
  periodStart: string;
  periodEnd: string;
  onPeriodStartChange: (value: string) => void;
  onPeriodEndChange: (value: string) => void;
  vendorUserId?: number;
  disabled?: boolean;
  showValidation?: boolean;
  showToastNotifications?: boolean;
  helperText?: {
    start?: string;
    end?: string;
  };
  error?: {
    start?: string;
    end?: string;
  };
  onToastNotification?: (message: string, severity: 'success' | 'error' | 'warning' | 'info') => void;
}

interface OverlappingInvoice {
  id: number;
  invoice_number: string;
  period_start: string;
  period_end: string;
  status: string;
}

const DateRangeSelector: React.FC<DateRangeSelectorProps> = ({
  periodStart,
  periodEnd,
  onPeriodStartChange,
  onPeriodEndChange,
  vendorUserId,
  disabled = false,
  showValidation = true,
  showToastNotifications = false,
  helperText = {
    start: 'First day of the billing period',
    end: 'Last day of the billing period'
  },
  error = {},
  onToastNotification
}) => {
  const [validationLoading, setValidationLoading] = useState(false);
  const [overlappingInvoice, setOverlappingInvoice] = useState<OverlappingInvoice | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [lastValidatedRange, setLastValidatedRange] = useState<{start: string, end: string} | null>(null);

  // Debounced validation
  useEffect(() => {
    if (!showValidation || !periodStart || !periodEnd || disabled) {
      setOverlappingInvoice(null);
      setValidationError(null);
      return;
    }

    // Skip validation if dates haven't changed
    if (lastValidatedRange && 
        lastValidatedRange.start === periodStart && 
        lastValidatedRange.end === periodEnd) {
      return;
    }

    // Skip validation if end date is not after start date
    if (new Date(periodEnd) <= new Date(periodStart)) {
      setOverlappingInvoice(null);
      setValidationError(null);
      return;
    }

    const timeoutId = setTimeout(async () => {
      try {
        setValidationLoading(true);
        setValidationError(null);

        const response = await api.post('/vendor-invoices/check-date-range', {
          period_start: periodStart,
          period_end: periodEnd,
          ...(vendorUserId && { vendor_user_id: vendorUserId })
        });

        if (response.data.success) {
          setOverlappingInvoice(response.data.overlapping_invoice);
          setLastValidatedRange({ start: periodStart, end: periodEnd });
          
          // Show toast notification for overlapping invoice
          if (response.data.overlapping_invoice && showToastNotifications && onToastNotification) {
            const invoice = response.data.overlapping_invoice;
            const message = `An invoice already exists for this date range. Please select a different period. (Invoice: ${invoice.invoice_number})`;
            onToastNotification(message, 'warning');
          }
        }
      } catch (err: any) {
        console.error('Date range validation error:', err);
        setValidationError('Failed to validate date range');
        setOverlappingInvoice(null);
      } finally {
        setValidationLoading(false);
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [periodStart, periodEnd, vendorUserId, showValidation, disabled, lastValidatedRange]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft': return 'default';
      case 'pending_approval': return 'warning';
      case 'approved': return 'info';
      case 'final': return 'success';
      case 'sent': return 'primary';
      case 'paid': return 'success';
      case 'overdue': return 'error';
      default: return 'default';
    }
  };

  // Using formatDateMedium from utils/date for New York timezone
  const formatDate = (dateString: string) => formatDateMedium(dateString);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
        <TextField
          label="Period Start Date"
          type="date"
          value={periodStart}
          onChange={(e) => onPeriodStartChange(e.target.value)}
          fullWidth
          required
          disabled={disabled}
          InputLabelProps={{ shrink: true }}
          helperText={helperText.start}
          error={!!error.start}
          FormHelperTextProps={{
            error: !!error.start
          }}
        />

        <TextField
          label="Period End Date"
          type="date"
          value={periodEnd}
          onChange={(e) => onPeriodEndChange(e.target.value)}
          fullWidth
          required
          disabled={disabled}
          InputLabelProps={{ shrink: true }}
          helperText={helperText.end}
          error={!!error.end}
          FormHelperTextProps={{
            error: !!error.end
          }}
        />
      </Box>

      {/* Validation Results */}
      {showValidation && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {validationLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                Checking date range availability...
              </Typography>
            </Box>
          )}

          {validationError && (
            <Alert severity="error" sx={{ py: 0.5 }}>
              {validationError}
            </Alert>
          )}

          {overlappingInvoice && !validationLoading && (
            <Alert 
              severity="warning" 
              icon={<WarningIcon />}
              sx={{ py: 0.5 }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="body2">
                  This date range overlaps with an existing invoice:
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Chip
                    label={overlappingInvoice.invoice_number}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                  <Typography variant="body2" color="text.secondary">
                    {formatDate(overlappingInvoice.period_start)} - {formatDate(overlappingInvoice.period_end)}
                  </Typography>
                  <Chip
                    label={overlappingInvoice.status.replace('_', ' ')}
                    size="small"
                    color={getStatusColor(overlappingInvoice.status)}
                    variant="outlined"
                  />
                </Box>
              </Box>
            </Alert>
          )}

          {!overlappingInvoice && !validationLoading && !validationError && 
           periodStart && periodEnd && new Date(periodEnd) > new Date(periodStart) && (
            <Alert severity="success" sx={{ py: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <DateRangeIcon fontSize="small" />
                <Typography variant="body2">
                  Date range is available for invoice creation
                </Typography>
              </Box>
            </Alert>
          )}
        </Box>
      )}

      {/* Error Messages */}
      {(error.start || error.end) && (
        <Alert severity="error" sx={{ py: 0.5 }}>
          <Typography variant="body2">
            {error.start || error.end}
          </Typography>
        </Alert>
      )}
    </Box>
  );
};

export default DateRangeSelector;
